"use client";

import { useEffect, useState, FC } from "react";
import style from "./style.module.scss";
import { IMetarData, IStationDetails, ITafData } from "@/interfaces/Map";
import MapService from "@/services/map";
import MetarDetails from "./MetarDetails";
import TafDetails from "./TafDetails";
import ForecastDetails from "./ForecastDetails";
import { IWeatherForecastData } from "@/interfaces/Map";
import GetApp from "./GetApp";
import Image from "next/image";
import { StationDetailOption, STATION_DETAIL_OPTIONS } from "@/types/options";
import { GENERAL, UI_TEXT, IMAGE_PATHS } from "@/constants";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Nullable } from "@/types";

interface Props {
  stationData: IStationDetails;
  onOpenDownloadPopup?: () => void;
  onClose?: () => void;
}

const StationDetails: FC<Props> = ({
  stationData,
  onOpenDownloadPopup,
  onClose,
}) => {
  const { getMetarByIcaoCode, getTafByIcaoCode, getWeatherForecast } =
    MapService;
  const [metar, setMetar] = useState<Nullable<IMetarData[]>>(null);
  const [activeOption, setActiveOption] = useState<StationDetailOption>(
    StationDetailOption.METAR
  );
  const [taf, setTaf] = useState<Nullable<ITafData[]>>(null);
  const [weatherForecast, setWeatherForecast] =
    useState<Nullable<IWeatherForecastData>>(null);
  const [isOpening, setIsOpening] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isLoadingMetar, setIsLoadingMetar] = useState(false);
  const [isLoadingTaf, setIsLoadingTaf] = useState(false);
  const [isLoadingForecast, setIsLoadingForecast] = useState(false);
  const [initialTabSet, setInitialTabSet] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsOpening(true);
    });
  }, []);

  const determineInitialTab = (
    metarData: Nullable<IMetarData[]>,
    tafData: Nullable<ITafData[]>,
    forecastData: Nullable<IWeatherForecastData>
  ) => {
    const hasMetar = metarData && metarData.length > 0;
    if (hasMetar) {
      return StationDetailOption.METAR;
    }

    const hasTaf = tafData && tafData.length > 0;
    if (hasTaf) {
      return StationDetailOption.TAF;
    }

    if (forecastData) {
      return StationDetailOption.FORECAST;
    }

    return StationDetailOption.METAR;
  };

  useEffect(() => {
    if (!stationData?.icao_code) return;

    const fetchMetar = async () => {
      setIsLoadingMetar(true);
      try {
        const metarData = (await getMetarByIcaoCode(
          stationData.icao_code
        )) as IMetarData[];
        setMetar(metarData);
        return metarData;
      } catch (error) {
        setMetar(null);
        return null;
      } finally {
        setIsLoadingMetar(false);
      }
    };

    const fetchTaf = async () => {
      setIsLoadingTaf(true);
      try {
        const tafData = (await getTafByIcaoCode(
          stationData.icao_code
        )) as ITafData[];
        setTaf(tafData);
        return tafData;
      } catch (error) {
        setTaf(null);
        return null;
      } finally {
        setIsLoadingTaf(false);
      }
    };

    const fetchWeatherForecast = async () => {
      setIsLoadingForecast(true);
      try {
        const weatherForecastData = (await getWeatherForecast(
          stationData.icao_code
        )) as IWeatherForecastData;
        setWeatherForecast(weatherForecastData);
        return weatherForecastData;
      } catch (error) {
        setWeatherForecast(null);
        return null;
      } finally {
        setIsLoadingForecast(false);
      }
    };

    setInitialTabSet(false);
    Promise.all([fetchMetar(), fetchTaf(), fetchWeatherForecast()]).then(
      ([metarData, tafData, forecastData]) => {
        const initialTab = determineInitialTab(
          metarData,
          tafData,
          forecastData
        );
        setActiveOption(initialTab);
        setInitialTabSet(true);
      }
    );
  }, [stationData?.icao_code]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose?.();
    }, GENERAL.CLOSE_ANIMATION_DELAY);
  };

  const showLearnMoreButton =
    activeOption === StationDetailOption.METAR ||
    activeOption === StationDetailOption.TAF ||
    activeOption === StationDetailOption.FORECAST;

  return (
    <div
      className={`${style.locationDetailsContainer} ${
        isClosing ? style.closing : isOpening ? style.opening : ""
      }`}
    >
      <div className={style.closeContainer}>
        <button className={style.closeButton} onClick={handleClose}>
          <Image
            src={IMAGE_PATHS.ICONS.BACK_ARROW}
            alt="close"
            width={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
            height={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
          />
        </button>
      </div>
      <div className={style.stationNameContainer}>
        <div className={style.circleIcon} />
        <h2 className={style.stationName}>{stationData?.name}</h2>
        <div className={style.stationCode}>{stationData?.icao_code}</div>
      </div>
      <div className={style.detailsOptions}>
        <div className={style.detailsOptionButtons}>
          {STATION_DETAIL_OPTIONS.map((option) => (
            <button
              key={option}
              className={`${style.detailsOptionButton} ${
                activeOption === option ? style.detailsOptionButtonActive : ""
              }`}
              onClick={() => setActiveOption(option)}
              type="button"
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {!initialTabSet &&
        (isLoadingMetar || isLoadingTaf || isLoadingForecast) && (
          <div className={style.loadingContainer}>
            <Spin
              indicator={<LoadingOutlined className={style.loadingIcon} spin />}
            />
          </div>
        )}
      {initialTabSet && activeOption === StationDetailOption.METAR && (
        <MetarDetails metar={metar} />
      )}
      {initialTabSet && activeOption === StationDetailOption.TAF && (
        <TafDetails taf={taf} />
      )}
      {initialTabSet && activeOption === StationDetailOption.FORECAST && (
        <ForecastDetails forecast={weatherForecast} />
      )}
      {activeOption === StationDetailOption.ATIS && <GetApp />}
      {activeOption === StationDetailOption.NOTAM && <GetApp />}

      {showLearnMoreButton && (
        <div className={style.learnMoreButtonContainer}>
          <button
            className={style.learnMoreButton}
            onClick={onOpenDownloadPopup}
          >
            {UI_TEXT.LABELS.LEARN_MORE}
          </button>
        </div>
      )}
    </div>
  );
};

export default StationDetails;
